import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User, Heart, Users, Baby, Sparkles, HeartHandshake, ShieldCheck,
  ClipboardList, Route, MessageCircle, MapPin, ArrowRight, Check, Star, Video, Building2,
} from "lucide-react";
import { Section, Reveal, Eyebrow, FaqList } from "@/components/Shared";
import { SITE, waLink, IMAGES } from "@/data/site";
import { SERVICES } from "@/data/services";
import { TOPIC_LIST } from "@/data/topics";
import { PSYCHOLOGISTS } from "@/data/psychologists";
import { TESTIMONIALS, GOOGLE_RATING } from "@/data/testimonials";

const ICONS = { User, Heart, Users, Baby, Sparkles, HeartHandshake, ShieldCheck, ClipboardList, Route, Video, Building2 };

const CARD_SERVICES = SERVICES.filter((s) =>
  ["individual", "couples", "child", "family", "assessment", "programs"].includes(s.key)
);

const serviceHref = (s, p, lang) => {
  if (s.key === "assessment") return p.assessment;
  if (s.key === "programs") return p.programs;
  return p.service(s.slug[lang]);
};

const HERO_TEXT_MOTION = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7 } };
const HERO_IMAGE_MOTION = { initial: { opacity: 0, scale: 0.97 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.8, delay: 0.15 } };

export const HeroSection = ({ lang, t, p }) => (
  <section className="relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-28">
      <motion.div {...HERO_TEXT_MOTION}>
        <Eyebrow>{SITE.legalName} · Renon, Denpasar</Eyebrow>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1F2937]" data-testid="hero-h1">
          {t.hero.h1}
        </h1>
        <p className="mt-6 text-base md:text-lg leading-relaxed text-[#4B5563] max-w-xl">{t.hero.sub}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Link to={p.booking} data-testid="hero-book-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#4A6B53] hover:bg-[#3A5542] text-white font-semibold px-8 py-4 transition-colors">
            {t.cta.book} <ArrowRight size={17} />
          </Link>
          <a href={waLink(lang === "id" ? "Halo Bali Empower, saya ingin berkonsultasi." : "Hello Bali Empower, I would like a consultation.")}
            target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#4A6B53] text-[#4A6B53] font-semibold px-8 py-4 hover:bg-[#4A6B53]/5 transition-colors">
            <MessageCircle size={17} /> {t.cta.whatsapp}
          </a>
        </div>
        <p className="mt-6 text-sm text-[#4B5563]">{t.hero.trust}</p>
        <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-maps-link"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#4A6B53] hover:underline">
          <MapPin size={15} /> {SITE.address.street}, {SITE.address.area}, {SITE.address.city}
        </a>
      </motion.div>
      <motion.div {...HERO_IMAGE_MOTION}
        className="hidden lg:block">
        <img src={IMAGES.session} alt={lang === "id" ? "Suasana konsultasi psikologi yang tenang dan suportif" : "A calm, supportive psychology consultation environment"}
          className="rounded-3xl object-cover w-full h-[560px]" loading="eager" />
      </motion.div>
    </div>
  </section>
);

export const TrustBar = ({ t }) => (
  <div className="border-y border-[#E5E1D8] bg-[#F2EFE9]" data-testid="trust-bar">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-wrap justify-center gap-x-8 gap-y-3">
      {t.trustBar.map((item) => (
        <span key={item} className="inline-flex items-center gap-2 text-sm text-[#1F2937]">
          <Check size={15} className="text-[#4A6B53]" /> {item}
        </span>
      ))}
    </div>
  </div>
);

export const ServicesGrid = ({ lang, t, p }) => (
  <Section data-testid="services-section">
    <Reveal>
      <Eyebrow>{t.nav.services}</Eyebrow>
      <h2 className="text-3xl sm:text-4xl tracking-tight text-[#1F2937] max-w-2xl">{t.sections.startHere}</h2>
    </Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {CARD_SERVICES.map((s, i) => {
        const Icon = ICONS[s.icon];
        return (
          <Reveal key={s.key} delay={i * 0.06}>
            <Link to={serviceHref(s, p, lang)} data-testid={`service-card-${s.key}`}
              className="be-card block h-full rounded-2xl border border-[#E5E1D8] bg-white p-8">
              <Icon size={26} className="text-[#4A6B53]" strokeWidth={1.6} />
              <h3 className="mt-5 text-xl font-display text-[#1F2937]">{s.name[lang]}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#4B5563]">{s.short[lang]}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#4A6B53]">
                {t.cta.learnMore} <ArrowRight size={14} />
              </span>
            </Link>
          </Reveal>
        );
      })}
    </div>
  </Section>
);

const TopicChip = ({ label, hasPage }) => (
  <span className={`inline-block rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${
    hasPage
      ? "bg-white border-[#4A6B53]/30 text-[#1F2937] hover:bg-[#4A6B53] hover:text-white"
      : "bg-white/60 border-[#E5E1D8] text-[#4B5563]"
  }`}>{label}</span>
);

export const TopicsSection = ({ lang, t, p }) => (
  <Section className="bg-[#F2EFE9] rounded-none" data-testid="problems-section">
    <Reveal>
      <Eyebrow>{t.nav.mentalHealth}</Eyebrow>
      <h2 className="text-3xl sm:text-4xl tracking-tight text-[#1F2937] max-w-2xl">{t.sections.problems}</h2>
      <p className="mt-4 text-[#4B5563] max-w-xl">{t.sections.problemsSub}</p>
    </Reveal>
    <div className="mt-10 flex flex-wrap gap-3">
      {TOPIC_LIST.map((topic) =>
        topic.hasPage ? (
          <Link key={topic.en} to={p.topic(topic.slug[lang])} data-testid={`topic-chip-${topic.slug.en}`}>
            <TopicChip label={topic[lang]} hasPage />
          </Link>
        ) : (
          <span key={topic.en}>
            <TopicChip label={topic[lang]} hasPage={false} />
          </span>
        )
      )}
    </div>
    <Link to={p.mentalHealth} data-testid="problems-explore-link"
      className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#4A6B53] hover:underline">
      {t.nav.mentalHealth} <ArrowRight size={15} />
    </Link>
  </Section>
);

export const WhySection = ({ t }) => (
  <Section data-testid="why-section">
    <Reveal>
      <Eyebrow>Bali Empower</Eyebrow>
      <h2 className="text-3xl sm:text-4xl tracking-tight text-[#1F2937]">{t.sections.why}</h2>
    </Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
      {t.why.map((w, i) => (
        <Reveal key={w.t} delay={i * 0.05}>
          <div className="flex gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-[#4A6B53]/10 flex items-center justify-center">
              <Check size={17} className="text-[#4A6B53]" />
            </div>
            <div>
              <h3 className="font-semibold font-sans text-[#1F2937]">{w.t}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[#4B5563]">{w.d}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

export const HowSection = ({ lang, t, p }) => (
  <Section className="bg-[#1F2937]" data-testid="how-section">
    <Reveal>
      <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-[#9CBFA6] mb-4">
        {lang === "id" ? "Cara Kerja" : "How It Works"}
      </p>
      <h2 className="text-3xl sm:text-4xl tracking-tight text-white">{t.sections.how}</h2>
    </Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
      {t.steps.map((s, i) => (
        <Reveal key={s.t} delay={i * 0.07}>
          <div>
            <span className="font-display text-4xl text-[#9CBFA6]">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-3 font-semibold font-sans text-white text-[15px]">{s.t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{s.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <div className="mt-12">
      <Link to={p.booking} data-testid="how-book-cta"
        className="inline-flex items-center gap-2 rounded-full bg-white text-[#1F2937] font-semibold px-8 py-4 hover:bg-[#F2EFE9] transition-colors">
        {t.cta.book} <ArrowRight size={16} />
      </Link>
    </div>
  </Section>
);

export const TeamSection = ({ lang, t, p }) => (
  <Section data-testid="team-section">
    <Reveal>
      <Eyebrow>{t.nav.psychologists}</Eyebrow>
      <h2 className="text-3xl sm:text-4xl tracking-tight text-[#1F2937]">{t.sections.team}</h2>
      <p className="mt-4 text-[#4B5563] max-w-xl">{t.sections.teamSub}</p>
    </Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {PSYCHOLOGISTS.map((psy, i) => (
        <Reveal key={psy.slug} delay={i * 0.06}>
          <Link to={p.psychologist(psy.slug)} data-testid={`psychologist-card-${i}`}
            className="be-card block rounded-2xl border border-[#E5E1D8] bg-white overflow-hidden">
            <div className="h-44 bg-[#F2EFE9] flex items-center justify-center overflow-hidden">
              {psy.photo ? <img src={psy.photo} alt={psy.name[lang]} className="w-full h-full object-cover" /> : <User size={44} className="text-[#C4A484]" strokeWidth={1.2} />}
            </div>
            <div className="p-6">
              <h3 className="font-display text-lg text-[#1F2937]">{psy.name[lang]}</h3>
              <p className="mt-1 text-sm text-[#4B5563]">{psy.title[lang]}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#4A6B53]">
                {t.misc.viewProfile} <ArrowRight size={13} />
              </span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </Section>
);

const TestimonialCard = ({ tm, lang }) => (
  <div className="rounded-2xl bg-white border border-[#E5E1D8] p-7" data-testid={`testimonial-card-${tm.name}`}>
    <div className="flex gap-1 mb-3">
      {Array.from({ length: tm.rating || 5 }).map((_, si) => (
        <Star key={`${tm.name}-star-${si}`} size={15} className="text-[#C4A484] fill-[#C4A484]" />
      ))}
    </div>
    <p className="text-sm leading-relaxed text-[#4B5563]">
      {typeof tm.text === "string" ? tm.text : tm.text[lang]}
    </p>
    <p className="mt-4 text-sm font-semibold text-[#1F2937]">{tm.name}</p>
    {tm.source && <p className="text-xs text-[#4B5563]">via {tm.source}</p>}
  </div>
);

export const TestimonialsSection = ({ lang }) => (
  <Section className="pt-0" data-testid="testimonials-section">
    <div className="rounded-3xl bg-[#F2EFE9] px-8 py-14 lg:px-16">
      <Eyebrow>{lang === "id" ? "Ulasan Klien" : "Client Reviews"}</Eyebrow>
      <h2 className="text-3xl sm:text-4xl tracking-tight text-[#1F2937] max-w-2xl">
        {lang === "id" ? "Apa Kata Klien Kami" : "What Our Clients Say"}
      </h2>
      {TESTIMONIALS.length > 0 ? (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((tm) => <TestimonialCard key={tm.name} tm={tm} lang={lang} />)}
        </div>
      ) : (
        <p className="mt-5 text-[#4B5563] max-w-2xl" data-testid="testimonials-honesty-note">
          {lang === "id"
            ? "Kami hanya menampilkan ulasan asli dari klien. Ulasan sedang dikumpulkan dan akan ditampilkan di sini — sementara itu, Anda dapat membaca ulasan kami langsung di Google."
            : "We only display genuine client reviews. Reviews are being collected and will appear here — in the meantime, you can read our reviews directly on Google."}
        </p>
      )}
      {GOOGLE_RATING && (
        <p className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1F2937]" data-testid="google-rating-badge">
          <Star size={16} className="text-[#C4A484] fill-[#C4A484]" />
          {GOOGLE_RATING.value} · {GOOGLE_RATING.count} {lang === "id" ? "ulasan Google" : "Google reviews"}
        </p>
      )}
      <div className="mt-8 flex flex-wrap gap-4">
        <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="testimonials-google-link"
          className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white text-sm font-medium px-6 py-3 hover:bg-[#3A5542] transition-colors">
          {lang === "id" ? "Baca Ulasan Kami di Google" : "Read Our Reviews on Google"} <ArrowRight size={14} />
        </a>
        <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="testimonials-write-link"
          className="inline-flex items-center gap-2 rounded-full border border-[#4A6B53] text-[#4A6B53] text-sm font-medium px-6 py-3 hover:bg-[#4A6B53]/5 transition-colors">
          {lang === "id" ? "Tulis Ulasan" : "Write a Review"}
        </a>
      </div>
    </div>
  </Section>
);

export const FaqPreview = ({ t, p, faqItems }) => (
  <Section className="pt-0" data-testid="home-faq-section">
    <div className="grid lg:grid-cols-3 gap-12">
      <Reveal>
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="text-3xl sm:text-4xl tracking-tight text-[#1F2937]">{t.sections.faq}</h2>
        <Link to={p.faq} data-testid="home-faq-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#4A6B53] hover:underline">
          {t.nav.faq} <ArrowRight size={15} />
        </Link>
      </Reveal>
      <div className="lg:col-span-2">
        <FaqList items={faqItems} testPrefix="home-faq" />
      </div>
    </div>
  </Section>
);

export const LocationStrip = ({ t, p }) => (
  <Section className="pt-0" data-testid="home-location-section">
    <div className="rounded-3xl border border-[#E5E1D8] bg-white overflow-hidden grid lg:grid-cols-2">
      <div className="p-10 lg:p-14">
        <Eyebrow>{t.nav.location}</Eyebrow>
        <h2 className="text-3xl tracking-tight text-[#1F2937]">{t.sections.location}</h2>
        <address className="not-italic mt-6 text-[#4B5563] leading-relaxed">
          {SITE.legalName}<br />
          {SITE.address.street}<br />
          {SITE.address.area}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}<br />
          {SITE.address.country}
        </address>
        <p className="mt-3 text-sm text-[#4B5563]">{t.misc.nearby}</p>
        <div className="mt-6 flex flex-wrap gap-4">
          <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="home-maps-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white text-sm font-medium px-6 py-3 hover:bg-[#3A5542] transition-colors">
            <MapPin size={15} /> {t.misc.openMaps}
          </a>
          <Link to={p.location} data-testid="home-location-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#E5E1D8] text-sm font-medium px-6 py-3 text-[#1F2937] hover:border-[#4A6B53] transition-colors">
            {t.cta.learnMore}
          </Link>
        </div>
      </div>
      <iframe title="Bali Empower Google Maps" src={SITE.mapsEmbed} loading="lazy"
        className="w-full min-h-[320px] border-0" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
    </div>
  </Section>
);
